import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import noop from 'lodash/noop';
import classNames from 'classnames';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import modalCloseSvg from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import './_SimpleModal.scss';

/**
 * A SimpleModal component. In order to use it you'll need to make sure you import the 'modals' component from
 * bim360-matrix-bootstrap-sass
 */
class SimpleModal extends React.Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      show: props.show
    };

    this._handleOnHide = this._handleOnHide.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.show !== this.props.show) {
      this.setState({ show: nextProps.show });
    }
  }

  /**
   * hides the modal - after the modal is hidden, onExited will be ran
   */
  _handleOnHide() {
    this.setState({ show: false });
    this.props.onHide();
  }

  _renderCloseButton() {
    if (this.props.disableModalExit) return null;

    return (
      <IconButton onClick={this._handleOnHide}>
        <SvgIcon width="15px" height="15px">
          {modalCloseSvg}
        </SvgIcon>
      </IconButton>
    );
  }

  /**
   * render function
   */
  render() {
    const noBackdrop = this.props.backdrop !== undefined;
    const exitDisabled = this.props.disableModalExit ? 'static' : true;
    const modalBackdrop = noBackdrop ? this.props.backdrop : exitDisabled;

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <Modal
            {...this.props.modalProps}
            onExited={this.props.onExited}
            bsClass={classNames(this.props.className, `SimpleModal--${theme}`, 'SimpleModal')}
            show={this.state.show}
            onHide={this._handleOnHide}
            container={this.props.container}
            backdrop={modalBackdrop}
            keyboard={!this.props.disableModalExit}
          >
            <Modal.Header {...this.props.modalHeaderProps}>
              <div className="SimpleModal__header-container">
                <div>{this.props.title}</div>
                {this._renderCloseButton()}
              </div>
            </Modal.Header>
            <Modal.Body {...this.props.modalBodyProps}>{this.props.children}</Modal.Body>
            {this.props.footer ? (
              <Modal.Footer {...this.props.modalFooterProps}>{this.props.footer}</Modal.Footer>
            ) : null}
          </Modal>
        )}
      </ThemeConsumer>
    );
  }
}

SimpleModal.propTypes = {
  /**
   * Whether to show the modal or not
   */
  show: PropTypes.bool,
  /**
   * A callback function for the modal exit event
   */
  onExited: PropTypes.func.isRequired,
  /**
   * A callback function for when trying to hide the modal
   */
  onHide: PropTypes.func,
  /**
   * Text for the modal title
   */
  title: PropTypes.node.isRequired,
  /**
   * Determines if the modal cannot be exited (exit button will be hidden and clickoutside will be disabled)
   */
  disableModalExit: PropTypes.bool,
  /**
   * The modal container component. Used for attaching the modal as a child component to the container.
   */
  container: PropTypes.object,
  /**
   * A custom className for the container element of the component
   */
  className: PropTypes.string,
  /**
   * Modal content
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /**
   * Optional footer
   */
  footer: PropTypes.node,
  /**
   * Determines the backdrop of modal.
   */
  backdrop: PropTypes.oneOf(['static', true, false]),
  /**
   * Allows to pass props directly to the Modal component
   */
  modalProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Header component
   */
  modalHeaderProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Body component
   */
  modalBodyProps: PropTypes.object,
  /**
   * Allows to pass props directly to the Modal.Footer component
   */
  modalFooterProps: PropTypes.object
};

SimpleModal.defaultProps = {
  show: true,
  onHide: noop
};
export default SimpleModal;
